import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Header } from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import api, { apiErr } from "../lib/api";
import { formatGs, MODALIDADES } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { CreditCard, Eye, Banknote, History } from "lucide-react";
import { toast } from "sonner";
import { SearchBox, matchQ } from "../components/SearchBox";

const MODALIDAD_LABEL = Object.fromEntries(MODALIDADES.map((m) => [m.id, m.label]));
const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#3B82F6] focus:ring-2 focus:ring-blue-100";

export default function Credito() {
  const { setMobileOpen } = useOutletContext();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [suc, setSuc] = useState("todas");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [detalle, setDetalle] = useState(null);
  const [pago, setPago] = useState({ monto: "", metodo: "efectivo", nota: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => { api.get(`/cuenta-corriente?sucursal=${suc}`).then(({ data }) => setRows(data)).catch(() => {}); }, [suc]);
  useEffect(() => { load(); }, [load]);

  const abrirDetalle = async (r) => {
    try {
      const { data } = await api.get("/cuenta-corriente/detalle", { params: { remitente: r.remitente } });
      setDetalle(data);
      setPago({ monto: "", metodo: "efectivo", nota: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const registrarPago = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/pagos-credito", { remitente_nombre: detalle.totales.remitente, monto: Number(pago.monto), metodo: pago.metodo, nota: pago.nota });
      toast.success(`Pago registrado. Saldo restante: ${formatGs(data.saldo_restante)}`);
      await abrirDetalle(detalle.totales);
      load();
    } catch (e2) { toast.error(apiErr(e2)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Header title="Estado de Cuenta" subtitle="Liquidaciones y crédito por remitente" onMenu={() => setMobileOpen(true)} />
      <main className="p-4 sm:p-6 lg:p-8">
        {isAdmin && (
          <div className="mb-4 flex flex-wrap gap-2" data-testid="cc-sucursal-filtros">
            {["todas", "ASU", "COV", "CDE", "ENC", "CAA"].map((s) => (
              <button key={s} onClick={() => setSuc(s)} data-testid={`cc-suc-${s}`}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${suc === s ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-slate-300 hover:bg-slate-50"}`}>
                {s === "todas" ? "Todas" : s}
              </button>
            ))}
          </div>
        )}
        <SearchBox value={q} onChange={setQ} placeholder="Buscar remitente…" testid="cc-search" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rows.filter(matchQ(q)).map((r) => (
            <div key={r.remitente} className="bg-white rounded-xl border border-[#E2E8F0] p-5 shadow-sm" data-testid={`cuenta-card-${r.remitente}`}>
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center"><CreditCard className="h-5 w-5 text-violet-600" /></div>
                <div>
                  <div className="font-bold text-[#0A1F44]">{r.remitente}</div>
                  <div className="text-xs text-[#64748B]">{r.cantidad} envíos</div>
                </div>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#64748B]">Liquidación pendiente (COD + envío pagado + crédito)</span><span className="font-bold text-[#0A1F44]">{formatGs(r.liquidacion_pendiente)}</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Liquidado histórico</span><span className="font-semibold text-emerald-700">{formatGs(r.liquidacion_historica)}</span></div>
                <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-[#64748B]">Crédito cargado</span><span className="font-semibold">{formatGs(r.credito_cargado)}</span></div>
                <div className="flex justify-between"><span className="text-[#64748B]">Pagos registrados</span><span className="font-semibold text-emerald-700">- {formatGs(r.pagos)}</span></div>
                <div className="flex justify-between rounded-lg bg-violet-50 border border-violet-200 px-3 py-2"><span className="font-semibold text-violet-800">Saldo crédito a cobrar</span><span className="font-extrabold text-violet-800">{formatGs(r.credito_saldo)}</span></div>
              </div>
              <button onClick={() => abrirDetalle(r)} data-testid={`ver-cuenta-${r.remitente}`} className="mt-4 w-full flex items-center justify-center gap-2 text-sm font-semibold border border-slate-300 rounded-lg py-2 hover:bg-slate-50"><Eye className="h-4 w-4" /> Ver estado de cuenta</button>
            </div>
          ))}
          {rows.length === 0 && <div className="col-span-full text-center py-12 text-[#64748B] bg-white rounded-xl border border-[#E2E8F0]">No hay movimientos de cuenta.</div>}
        </div>
      </main>

      <Dialog open={!!detalle} onOpenChange={() => setDetalle(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">Estado de cuenta · {detalle?.totales.remitente}</DialogTitle>
            <DialogDescription>Liquidaciones, crédito y pagos del remitente.</DialogDescription>
          </DialogHeader>
          {detalle && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-sm font-extrabold text-[#0A1F44]">{formatGs(detalle.totales.liquidacion_pendiente)}</div><div className="text-[10px] text-[#64748B]">Liq. pendiente</div></div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-sm font-extrabold text-emerald-700">{formatGs(detalle.totales.liquidacion_historica)}</div><div className="text-[10px] text-[#64748B]">Liquidado</div></div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3"><div className="text-sm font-extrabold">{formatGs(detalle.totales.credito_cargado)}</div><div className="text-[10px] text-[#64748B]">Crédito cargado</div></div>
                <div className="rounded-lg bg-violet-50 border border-violet-200 p-3"><div className="text-sm font-extrabold text-violet-800">{formatGs(detalle.totales.credito_saldo)}</div><div className="text-[10px] text-violet-700">Saldo a cobrar</div></div>
              </div>

              {detalle.totales.credito_saldo > 0 && (
                <form onSubmit={registrarPago} className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-3">
                  <div className="flex items-center gap-2 font-semibold text-emerald-800 text-sm"><Banknote className="h-4 w-4" /> Registrar pago (saldar crédito)</div>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" className={input} placeholder={`Monto (máx. ${formatGs(detalle.totales.credito_saldo)})`} value={pago.monto} onChange={(e) => setPago({ ...pago, monto: e.target.value })} required data-testid="pago-monto-input" />
                    <select className={input} value={pago.metodo} onChange={(e) => setPago({ ...pago, metodo: e.target.value })}>
                      <option value="efectivo">Efectivo</option>
                      <option value="transferencia">Transferencia</option>
                      <option value="deposito">Depósito</option>
                    </select>
                  </div>
                  <input className={input} placeholder="Nota (opcional)" value={pago.nota} onChange={(e) => setPago({ ...pago, nota: e.target.value })} />
                  <button disabled={saving} data-testid="pago-submit-btn" className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-2.5 text-sm disabled:opacity-60">{saving ? "Registrando..." : "Registrar pago"}</button>
                </form>
              )}

              <div>
                <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-2">Envíos</h4>
                <div className="rounded-lg border border-slate-200 overflow-hidden max-h-56 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-[#64748B] uppercase sticky top-0"><tr><th className="text-left px-3 py-2">Código</th><th className="text-left px-3 py-2">Modalidad</th><th className="text-right px-3 py-2">Producto</th><th className="text-right px-3 py-2">Flete</th><th className="text-left px-3 py-2">Estado</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {detalle.envios.map((e) => (
                        <tr key={e.id}>
                          <td className="px-3 py-2 font-mono font-bold">{e.code}</td>
                          <td className="px-3 py-2">{MODALIDAD_LABEL[e.modalidad] || e.modalidad}{e.liquidado ? " · liquidado" : ""}</td>
                          <td className="px-3 py-2 text-right">{formatGs(e.valor_producto)}</td>
                          <td className="px-3 py-2 text-right">{formatGs(e.costo_envio)}</td>
                          <td className="px-3 py-2"><StatusBadge status={e.status} /></td>
                        </tr>
                      ))}
                      {detalle.envios.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-[#64748B]">Sin envíos.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-[#64748B] uppercase tracking-wide mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Historial de pagos</h4>
                {detalle.pagos.length === 0 ? <p className="text-sm text-[#64748B]">Sin pagos registrados.</p> : (
                  <div className="space-y-1.5">
                    {detalle.pagos.map((p) => (
                      <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                        <span className="text-[#64748B]">{new Date(p.created_at).toLocaleDateString("es-PY")} · {p.metodo}{p.nota ? ` · ${p.nota}` : ""}</span>
                        <span className="font-bold text-emerald-700">{formatGs(p.monto)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
